/**
 * Internal Interviews API Service (admin surface).
 *
 * The interview period ended 2026-10-07; the user app's public tracker page
 * and its own copy of this service (packages/frontend/src/services/interviews.ts)
 * were removed. This admin-only copy — the only one left — is unchanged.
 */

import { api } from "@/lib/api";

export interface InterviewResponse {
  question: string;
  answer: string;
}

export interface TeamMember {
  id: string;
  team: string;
  name: string;
  role: string;
  status: string;
  rewardGiven: string;
  email: string;
  whatsApp: string;
  /** Admin-only free-form notes. */
  notes: string;
  /** AI-generated insights from the interview. */
  aiInsights: string;
  interviewResponses: InterviewResponse[];
  interviewDate: string;
  interviewer: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTeamMemberInput {
  team: string;
  name: string;
  role?: string;
  email?: string;
  whatsApp?: string;
  status?: string;
  rewardGiven?: string;
  notes?: string;
  aiInsights?: string;
  interviewResponses?: InterviewResponse[];
  interviewDate?: string;
  interviewer?: string;
}

export interface UpdateTeamMemberInput {
  team?: string;
  name?: string;
  role?: string;
  email?: string;
  whatsApp?: string;
  status?: string;
  rewardGiven?: string;
  notes?: string;
  aiInsights?: string;
  interviewResponses?: InterviewResponse[];
  interviewDate?: string;
  interviewer?: string;
}

export const interviewsApi = {
  /** Admin: list team members (full fields) */
  listAdmin: () => api.get<TeamMember[]>("/admin/internal-interviews"),

  /** Admin: get a single team member by ID */
  getAdmin: (id: string) =>
    api.get<TeamMember>(`/admin/internal-interviews/${id}`),

  /** Admin: create a team member */
  create: (input: CreateTeamMemberInput) =>
    api.post<TeamMember>("/admin/internal-interviews", input),

  /** Admin: update a team member */
  update: (id: string, input: UpdateTeamMemberInput) =>
    api.patch<TeamMember>(`/admin/internal-interviews/${id}`, input),

  /** Admin: delete a team member */
  remove: (id: string) => api.delete<void>(`/admin/internal-interviews/${id}`),
};
